import React, { useContext, useCallback } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import { createPortal } from 'react-dom';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS, NEW_USED_TYPES } from 'globals.js';
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { apiFetch, formatFunctions, makePath } from 'functions.js';

// LOCAL VARIABLES
import { WARRANTY_TYPES_OPTION_KEYS } from '../../../../../sections/variables';
import { LOCATION_TYPE } from '../../../options';

// HELPERS
import { RecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import Form from 'core/form/Form';

// PARTS
import Card from 'parts/card/Card';
import Sidebar from 'parts/sidebar/Sidebar';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';
import LocationUpdateSidebar from '../tools/LocationUpdateSidebar';

// MAIN COMPONENT
const VehicleInfoBody = ({ inventoryId }) => {
  const { values, setFieldValue } = useFormikContext() || {};
  const { refetchRecord } = useContext(RecordContext) || {};

  const { located_at_dealership, location_arrival_date, location_type_id, new_used, vin_num, warranty_type, warranty_type_name } =
    values || {};

  const isNew = new_used === NEW_USED.new.numeric;
  const newUsedLabel = NEW_USED_TYPES[new_used];
  const isLocatedAtDealership = located_at_dealership && +located_at_dealership === 1;

  const handleSelect = useCallback(
    warrantyId => {
      apiFetch({
        method: 'PUT',
        params: {
          warrantyType: warrantyId
        },
        endpoint: makePath(ENDPOINTS.inventory.updateWarrantyType, inventoryId),
        loadingMessage: 'Updating warranty.',
        successMessage: 'Warranty updated.',
        errorMessage: 'Error updating your warranty.',
        onSuccess: () => {
          refetchRecord();
          setFieldValue('warranty_type', warrantyId);
        }
      });
    },
    [inventoryId, refetchRecord, setFieldValue]
  );

  return (<>
    <Form.Body plaintext>
      <Form.Control label="Stock #" name="stock_num" copyToClipboard />
      <Form.Control label="VIN #" name="vin_num" copyToClipboard />
      {!!vin_num &&
        <Attachment.Upload
          label="Photo Proof of VIN"
          name="documentAttachment.vin_photo"
          documentType={isNew ? INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_USED}
        />
      }
      <Form.Control label="Type" value={newUsedLabel} plaintext />
      { newUsedLabel === 'Pre-Owned' &&
        warranty_type_name === 'CPO Warranty' &&
        <Form.Control label="Subtype" value='Certified Pre-Owned (CPO)' plaintext />
      }
      <Sidebar.YesNo
        sidebarName="locationUpdate"
        label="Is the Vehicle Physically At the Dealership?"
        value={located_at_dealership}
        plaintext={false}
        numberic
      />

      <Form.Control
        label="Location"
        value={
          location_type_id
            ? `${LOCATION_TYPE[location_type_id - 1].name} (${LOCATION_TYPE[location_type_id - 1].key})`
            : '-'
        }
      />

      {!isLocatedAtDealership ? (
        <Form.Control value={formatFunctions.date(location_arrival_date)} label="Estimated Date of Arrival for Physical Check-in" />
      ) : null}
      <Form.Textarea label="Location Comments" name="location_comments"/>
      <Form.Control name="vehicle_year" label="Year" schema="year" />
      <Form.Control name="make" label="Make" />
      <Form.Control name="model" label="Model" />
      <Form.Control name="model_code" label="Model Code" />
      <Form.Control name="vehicle_trim" label="Trim" copyToClipboard />
      <Form.Control name="body_style" label="Body Style" />
      <Form.Control name="engine" label="Engine" />
      <Form.Control name="engine_type" label="Engine Type" />
      <Form.Control name="transmission_name" label="Transmission" />
      <Form.Control name="drivetrain_name" label="Drivetrain" />

      <Form.Control name="ext_color" label="Exterior" copyToClipboard />
      <Form.Control name="int_color" label="Interior" copyToClipboard />
      <Form.Select
        value={warranty_type}
        onSelect={handleSelect}
        optionKeys={WARRANTY_TYPES_OPTION_KEYS}
        label="Warranty Type"
        lookup="warrantyTypes"
        plaintext={false}
      />
      <Form.Control name="mileage" label="Mileage" schema="miles" copyToClipboard />
      <Attachment.Upload
        label="Photo Proof of Mileage"
        name="documentAttachment.mileage_photo"
        documentType={isNew ? INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_USED}
      />
    </Form.Body>
    {createPortal(
      <LocationUpdateSidebar />,
      document.body
    )}
  </>);
};
// EXPORT
export default function VehicleInfo(props) {
  return <Card title="Vehicle Info" body={<VehicleInfoBody {...props} />} />;
}
